import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  List,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import MoveUpTwoToneIcon from "@mui/icons-material/MoveUpTwoTone";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import { ToastService } from "src/Shared/Services";
import useDebounce from "src/Shared/Hooks/UseDebounce.hook";
import CompanyService from "../../Services/Company.service";
import { ICompanySearchFilter } from "../../Interfaces/ICompanySearchFilter";
import { STATUS_STYLES } from "../CompanyManagementLayout/CompanyHeader/CompanyHeader.config";
import { getSearchTargetPath, isSearchTermValid } from "./ClientSearch.utils";

type Props = {
  /** The tab the search is rendered on, to hint at matches living on the other tab. */
  currentTab: "home" | "prospects";
};

const MAX_RESULTS = 12;

/**
 * The CRM's type-ahead client search, extended to span Home and Prospects
 * (P.4, decision 17) — one control, updated in place; no second search added.
 */
const ClientSearch: React.FC<Props> = ({ currentTab }) => {
  const navigate = useNavigate();
  const [term, setTerm] = useState<string>("");
  const [results, setResults] = useState<ICompanySearchFilter[]>([]);
  const [searched, setSearched] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [highlight, setHighlight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useDebounce(
    () => {
      if (isSearchTermValid(term)) {
        search(term.trim());
      } else {
        setResults([]);
        setSearched(false);
        setOpen(false);
      }
    },
    400,
    [term],
  );

  const search = (value: string) => {
    setLoading(true);
    CompanyService.getCompanySearch(value, MAX_RESULTS)
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (data) => {
          setResults(data || []);
          setSearched(true);
          setHighlight(0);
          setOpen(true);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  /**
   * The typed term is left in the field, whether the pick was a click or Enter: it is what the
   * shown-as-focused row on the grid actually matches, and clearing it made the search look like it
   * had been thrown away rather than acted on (bug 5821).
   */
  const onSelect = (option: ICompanySearchFilter) => {
    setResults([]);
    setSearched(false);
    setOpen(false);

    // Search no longer returns archived legacy records (bug 5837), so this branch — and the
    // isArchived handling in tabHint and the row below — is dormant rather than dead: it is the only
    // implementation of the archived-transfer entry point, kept for whatever re-exposes it.
    if (option.isArchived) {
      navigate(`/company/archived/${option.value}`);
      return;
    }
    navigate(getSearchTargetPath(option), {
      state: { companyName: option.label },
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    // Arrow keys move the highlight and wrap; Enter selects; Escape closes.
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((highlight + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((highlight - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onSelect(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const tabHint = (option: ICompanySearchFilter): string | null => {
    if (option.isArchived) return null;
    const optionTab = option.isProspectStage ? "prospects" : "home";
    if (optionTab === currentTab) return null;
    return option.isProspectStage ? "On Prospects" : "On Home";
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ width: 350 }}>
        <TextField
          fullWidth
          label="Search Client"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => searched && setOpen(true)}
          InputProps={{
            endAdornment: loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : null,
          }}
        />
        <Popper
          open={open && searched}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: 420 }}
        >
          <Paper elevation={4}>
            {results.length === 0 ? (
              <Typography sx={{ p: 2 }} variant="body2">
                No matches for “{term.trim()}”
              </Typography>
            ) : (
              <List dense>
                {results.map((option, index) => {
                  const hint = tabHint(option);
                  const styles =
                    STATUS_STYLES[option.status?.toLowerCase() || "default"] ||
                    STATUS_STYLES["default"];
                  return (
                    <ListItemButton
                      key={`${option.isArchived ? "A" : "E"}-${option.value}`}
                      selected={index === highlight}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => onSelect(option)}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {option.label}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {hint && (
                              <Chip label={hint} size="small" variant="outlined" />
                            )}
                            {option.isArchived ? (
                              <MoveUpTwoToneIcon color="primary" fontSize="small" />
                            ) : (
                              option.status && (
                                <Chip
                                  label={option.status}
                                  size="small"
                                  sx={{
                                    backgroundColor: styles.chipBg,
                                    color: styles.chipColor,
                                    fontWeight: 600,
                                  }}
                                />
                              )
                            )}
                          </Box>
                        </Box>
                        {!option.isArchived && (
                          <Typography variant="caption" color="text.secondary">
                            {option.region || "No region"} ·{" "}
                            {option.relationshipManager || "Unassigned"}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ClientSearch;
